import React, { useEffect, useState } from "react";
import Lottie from "react-lottie-player";

import { Box, Typography, useTheme } from "@mui/material";

import {
  errorRed,
  greenDark,
  mintGreen,
  orange,
  tintOrange,
  tintRed,
} from "../../../design-system/colors";
import { EditPenIcon, InfoIcon } from "../../../design-system/icons";
import CircleGreenCheckIcon from "design-system/icons/CircleGreenCheckIcon";
import {
  useScreenSize,
  usePrevious,
  useOperator,
} from "../../../globals/hooks";
import MoovsBanner, { MoovsBannerProps } from "../../globals/MoovsBanner";
import MoovsDialog from "../../globals/MoovsDialog";
import mailCheck from "../../../illustrations/mailCheckBlue.svg";
import { SubmitSuccessLottie } from "design-system/lottie";
import RequestChangesToTripDialog from "../RequestChangesToTripDialog/RequestChangesToTripDialog";
import { Trip } from "types";
import RequestCancellationToTripDialog from "../RequestCancellationToTripDialog";

type SolicitChangeBannerProps = {
  trip: Trip;
  isReservationRequest?: boolean;
};

function SolicitChangeBanner(props: SolicitChangeBannerProps) {
  const { trip, isReservationRequest } = props;
  const { canAutomateCustomerChange, canRequestTripEdit } = trip;

  // hooks
  const { isMobileView } = useScreenSize();
  const prevTripId = usePrevious(trip.id);
  const theme = useTheme();
  const { operator } = useOperator();

  // state
  const [isSolicitUpdateDialogOpen, setIsSolicitUpdateDialogOpen] =
    useState<boolean>(false);
  const [
    isSolicitUpdateEmailSentDialogOpen,
    setIsSolicitUpdateEmailSentDialogOpen,
  ] = useState<boolean>(false);
  const [hasAutomatedTripChange, setHasAutomatedTripChange] =
    useState<boolean>(false);
  const [isTripCancellationDialogOpen, setIsTripCancellationDialogOpen] =
    useState<boolean>(false);

  // derived state
  const showCancellationRequestActionButton =
    !!operator?.settings?.customerTripCancellationRequestEnabled &&
    !trip.cancelledAt;
  const showCancellationRequestPendingBanner =
    trip.cancellationRequestedAt && !trip.cancelledAt;
  const hasSolicitedTripChange = !!trip.tripChangeProposal;

  // event handlers
  const handleBannerClick = () => {
    if (canAutomateCustomerChange) {
      setHasAutomatedTripChange(true);
    }
  };

  const handleSuccessfulSolicit = () => {
    handleBannerClick();
    setIsSolicitUpdateDialogOpen(false);
    setIsSolicitUpdateEmailSentDialogOpen(true);
  };

  const handleCloseUpdateTripDialog = () => {
    setIsSolicitUpdateDialogOpen(false);
  };

  const handleCloseTripCancellationDialog = () => {
    setIsTripCancellationDialogOpen(false);
  };

  // effects
  // reset trip shape on dialog close
  useEffect(() => {
    if (prevTripId !== trip.id) {
      setHasAutomatedTripChange(false);
    }
  }, [prevTripId, trip.id]);

  let solicitBannerProps: MoovsBannerProps;
  if (showCancellationRequestPendingBanner) {
    solicitBannerProps = {
      bannerText: "The cancellation has been requested and is pending approval",
      bannerTextColor: errorRed,
      bannerBgColor: tintRed,
    };
  } else if (hasAutomatedTripChange) {
    solicitBannerProps = {
      bannerText: "All changes have been successfully updated!",
      bannerBgColor: mintGreen,
      bannerIcon: <CircleGreenCheckIcon />,
      bannerTextColor: greenDark,
    };
  } else if (hasSolicitedTripChange) {
    solicitBannerProps = {
      bannerText: "Trip changes have been requested and are pending approval.",
      bannerBgColor: tintOrange,
      bannerIcon: <InfoIcon color={orange} />,
    };
  } else if (trip.hasRouteDispatchLogs) {
    solicitBannerProps = {
      bannerText:
        "Can not make a trip change proposal after the trip has already been dispatched.",
      bannerBgColor: tintOrange,
      bannerIcon: <InfoIcon color={orange} />,
    };
  } else {
    solicitBannerProps = {
      bannerText: "Need to make an update to your trip?",
      bannerBgColor: theme.palette.primary.light,
      actionButtonText: `${
        canAutomateCustomerChange ? "Make" : "Request"
      } Changes`,
      onActionButtonClick: () => setIsSolicitUpdateDialogOpen(true),
      actionButtonStartIcon: <EditPenIcon />,
      actionButtonVariant: "outlined",
      // show cancel trip button if operator has enabled it
      ...(showCancellationRequestActionButton && {
        secondaryActionButtonProps: {
          btnText: `${
            isReservationRequest ? `Cancel` : `Request to cancel`
          } trip`,
          onBtnClick: () => setIsTripCancellationDialogOpen(true),
        },
      }),
    };
  }

  if (
    (!canRequestTripEdit && !showCancellationRequestPendingBanner) ||
    !!trip.cancelledAt
  ) {
    return null;
  }

  return (
    <Box mb={3}>
      <MoovsBanner
        {...solicitBannerProps}
        {...(isMobileView && { flexDirection: "column" })}
      />
      <RequestChangesToTripDialog
        open={isSolicitUpdateDialogOpen}
        onSuccessfulSolicit={handleSuccessfulSolicit}
        onClose={handleCloseUpdateTripDialog}
        trip={trip}
      />
      <RequestCancellationToTripDialog
        open={isTripCancellationDialogOpen}
        onClose={handleCloseTripCancellationDialog}
        trip={trip}
        isReservationRequest={isReservationRequest}
      />
      <MoovsDialog
        open={isSolicitUpdateEmailSentDialogOpen}
        dialogTitle={`${
          canAutomateCustomerChange ? "Make" : "Request"
        } Changes to Trip`}
        size="sm"
        onClose={() => setIsSolicitUpdateEmailSentDialogOpen(false)}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          py={3}
          {...(isMobileView && { height: "100%", justifyContent: "center" })}
        >
          {canAutomateCustomerChange ? (
            <Lottie
              animationData={SubmitSuccessLottie}
              play
              loop={false}
              segments={[0, 112]}
              style={{
                height: "150px",
              }}
            />
          ) : (
            <img src={mailCheck} alt="Swoop" style={{ height: "98px" }} />
          )}
          <Box pt={3} pb={1}>
            <Typography variant="h3">
              {canAutomateCustomerChange
                ? "Changes confirmed!"
                : "Your request has been sent."}
            </Typography>
          </Box>
          <Box pb={2}>
            <Typography variant="body2" color="textSecondary">
              {canAutomateCustomerChange
                ? "Your trip updates have successfully been made."
                : "You will hear back if your changes are approved."}
            </Typography>
          </Box>
        </Box>
      </MoovsDialog>
    </Box>
  );
}

export default SolicitChangeBanner;
